import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '@store/hooks'
import {
  DEFAULT_CHARTING_SYSTEM,
  DEFAULT_TREATMENT_PLAN,
  isChartingPreferenceYes,
} from '@constants/Settings/chartingPreferences.constants'
import { fetchChartingPreferencesApi } from '@services/api/charting-preferences.api'
import {
  IChartingSystemPreferences,
  ITreatmentPlanPreferences,
  ChartingLayerKey,
} from '@dataTypes/chartingPreferences.type'
import { formatToothNumberForDisplay } from '@utils/tooth-number-display.utils'
import { normalizeDefaultChartingView } from '@utils/default-charting-view.utils'
import { logger } from '@utils/safeLogger'

interface ClinicChartingPreferencesCache {
  clinicId: string
  chartingSystem: IChartingSystemPreferences
  treatmentPlan: ITreatmentPlanPreferences
}

let preferencesCache: ClinicChartingPreferencesCache | null = null
let preferencesFetchPromise: Promise<ClinicChartingPreferencesCache> | null =
  null
let inFlightClinicId: string | null = null
const preferencesListeners = new Set<() => void>()

const notifyPreferencesListeners = (): void => {
  preferencesListeners.forEach((listener) => listener())
}

export const invalidateClinicChartingPreferencesCache = (): void => {
  preferencesCache = null
  preferencesFetchPromise = null
  inFlightClinicId = null
  notifyPreferencesListeners()
}

/** Push fresh prefs into the shared cache and notify mounted consumers. */
export const hydrateClinicChartingPreferencesCache = (
  clinicId: string,
  chartingSystem: IChartingSystemPreferences,
  treatmentPlan: ITreatmentPlanPreferences,
): void => {
  if (!clinicId) return
  preferencesCache = {
    clinicId,
    chartingSystem: {
      ...chartingSystem,
      default_charting_view: normalizeDefaultChartingView(
        chartingSystem.default_charting_view as string | ChartingLayerKey[],
      ),
    },
    treatmentPlan: { ...treatmentPlan },
  }
  preferencesFetchPromise = null
  inFlightClinicId = null
  notifyPreferencesListeners()
}

const resolveClinicId = (selectedClinicId: string): string =>
  selectedClinicId ||
  (typeof localStorage !== 'undefined'
    ? localStorage.getItem('clinic_id') || ''
    : '')

const getCachedPreferences = (
  clinicId: string,
): ClinicChartingPreferencesCache | null => {
  if (!clinicId || preferencesCache?.clinicId !== clinicId) {
    return null
  }
  return preferencesCache
}

const loadPreferencesForClinic = async (
  clinicId: string,
): Promise<ClinicChartingPreferencesCache> => {
  if (preferencesCache?.clinicId === clinicId) {
    return preferencesCache
  }
  if (preferencesFetchPromise && inFlightClinicId === clinicId) {
    return preferencesFetchPromise
  }
  inFlightClinicId = clinicId
  preferencesFetchPromise =
    (async (): Promise<ClinicChartingPreferencesCache> => {
      try {
        const res = await fetchChartingPreferencesApi(clinicId)
        const prefs = res.data?.charting_preferences
        const next: ClinicChartingPreferencesCache = {
          clinicId,
          chartingSystem: prefs?.charting_system
            ? {
                ...prefs.charting_system,
                default_charting_view: normalizeDefaultChartingView(
                  prefs.charting_system.default_charting_view as
                    | string
                    | ChartingLayerKey[],
                ),
              }
            : { ...DEFAULT_CHARTING_SYSTEM },
          treatmentPlan: prefs?.treatment_plan
            ? { ...prefs.treatment_plan }
            : { ...DEFAULT_TREATMENT_PLAN },
        }
        preferencesCache = next
        return next
      } catch (error) {
        logger.error('loadPreferencesForClinic', error)
        const fallback: ClinicChartingPreferencesCache = {
          clinicId,
          chartingSystem: { ...DEFAULT_CHARTING_SYSTEM },
          treatmentPlan: { ...DEFAULT_TREATMENT_PLAN },
        }
        preferencesCache = fallback
        return fallback
      } finally {
        preferencesFetchPromise = null
        inFlightClinicId = null
      }
    })()
  return preferencesFetchPromise
}

export const prefetchClinicChartingPreferences = (
  clinicId: string,
): Promise<ClinicChartingPreferencesCache | null> => {
  if (!clinicId) {
    return Promise.resolve(null)
  }
  return loadPreferencesForClinic(clinicId)
}

export interface UseClinicChartingPreferencesReturn {
  chartingSystem: IChartingSystemPreferences
  treatmentPlan: ITreatmentPlanPreferences
  /** False until clinic preferences have been loaded at least once. */
  isReady: boolean
  showToothNumber: boolean
  showMissingTeeth: boolean
  showPrimaryTeeth: boolean
  showExistingConditions: boolean
  showCompletedProcedures: boolean
  allowManualFeeOverride: boolean
  showInsuranceEstimate: boolean
  autoMarkCompletedWhenCharted: boolean
  formatToothNumber: (universalToothNumber: number) => string
  loading: boolean
  refresh: () => Promise<void>
}

export const useClinicChartingPreferences =
  (): UseClinicChartingPreferencesReturn => {
    const selectedClinicId = useAppSelector(
      (state) => state.clinic.selectedClinic?.clinic_id ?? '',
    )
    const clinicId = resolveClinicId(selectedClinicId)
    const initialCache = getCachedPreferences(clinicId)
    const [chartingSystem, setChartingSystem] =
      useState<IChartingSystemPreferences>(() =>
        initialCache
          ? { ...initialCache.chartingSystem }
          : { ...DEFAULT_CHARTING_SYSTEM },
      )
    const [treatmentPlan, setTreatmentPlan] =
      useState<ITreatmentPlanPreferences>(() =>
        initialCache
          ? { ...initialCache.treatmentPlan }
          : { ...DEFAULT_TREATMENT_PLAN },
      )
    const [isReady, setIsReady] = useState<boolean>(() => Boolean(initialCache))
    const [loading, setLoading] = useState<boolean>(() =>
      Boolean(clinicId && !initialCache),
    )

    const applyCache = useCallback(
      (cache: ClinicChartingPreferencesCache): void => {
        setChartingSystem(cache.chartingSystem)
        setTreatmentPlan(cache.treatmentPlan)
        setIsReady(true)
      },
      [],
    )

    const refresh = useCallback(async (): Promise<void> => {
      if (!clinicId) {
        setChartingSystem({ ...DEFAULT_CHARTING_SYSTEM })
        setTreatmentPlan({ ...DEFAULT_TREATMENT_PLAN })
        setIsReady(true)
        return
      }
      preferencesCache = null
      setIsReady(false)
      setLoading(true)
      try {
        const cache = await loadPreferencesForClinic(clinicId)
        applyCache(cache)
      } finally {
        setLoading(false)
      }
    }, [applyCache, clinicId])

    useEffect(() => {
      if (!clinicId) {
        setChartingSystem({ ...DEFAULT_CHARTING_SYSTEM })
        setTreatmentPlan({ ...DEFAULT_TREATMENT_PLAN })
        setIsReady(true)
        setLoading(false)
        return
      }
      let cancelled = false
      const syncFromSharedCache = (): void => {
        if (cancelled) return
        const cached = getCachedPreferences(clinicId)
        if (cached) {
          applyCache(cached)
          setLoading(false)
          return
        }
        setIsReady(false)
        setLoading(true)
        void loadPreferencesForClinic(clinicId).then((cache) => {
          if (!cancelled) {
            applyCache(cache)
            setLoading(false)
          }
        })
      }
      syncFromSharedCache()
      preferencesListeners.add(syncFromSharedCache)
      return () => {
        cancelled = true
        preferencesListeners.delete(syncFromSharedCache)
      }
    }, [applyCache, clinicId])

    const showToothNumber =
      isReady && chartingSystem.show_tooth_number === 'yes'
    const showMissingTeeth =
      !isReady || chartingSystem.show_missing_teeth === 'yes'
    const showPrimaryTeeth =
      !isReady || chartingSystem.show_primary_teeth === 'yes'
    const showExistingConditions =
      isReady && chartingSystem.show_existing_conditions === 'yes'
    const showCompletedProcedures =
      isReady && chartingSystem.show_completed_procedures === 'yes'
    const allowManualFeeOverride =
      isReady &&
      isChartingPreferenceYes(treatmentPlan.allow_manual_fee_override)
    const showInsuranceEstimate =
      !isReady || isChartingPreferenceYes(treatmentPlan.show_insurance_estimate)
    const autoMarkCompletedWhenCharted =
      isReady &&
      isChartingPreferenceYes(treatmentPlan.auto_mark_completed_when_charted)
    const formatToothNumber = useCallback(
      (universalToothNumber: number): string =>
        formatToothNumberForDisplay(
          universalToothNumber,
          chartingSystem.tooth_numbering_system,
        ),
      [chartingSystem.tooth_numbering_system],
    )

    return useMemo(
      () => ({
        chartingSystem,
        treatmentPlan,
        isReady,
        showToothNumber,
        showMissingTeeth,
        showPrimaryTeeth,
        showExistingConditions,
        showCompletedProcedures,
        allowManualFeeOverride,
        showInsuranceEstimate,
        autoMarkCompletedWhenCharted,
        formatToothNumber,
        loading,
        refresh,
      }),
      [
        chartingSystem,
        treatmentPlan,
        isReady,
        showToothNumber,
        showMissingTeeth,
        showPrimaryTeeth,
        showExistingConditions,
        showCompletedProcedures,
        allowManualFeeOverride,
        showInsuranceEstimate,
        autoMarkCompletedWhenCharted,
        formatToothNumber,
        loading,
        refresh,
      ],
    )
  }
